
import { defineComponent, reactive, ref, toRefs, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  ElMessage,
  FormInstance,
  FormRules,
  UploadFile,
  UploadProps,
  UploadUserFile,
} from 'element-plus'
import { INPUT_NAMES } from '@/widgets/WOrganizationCreationForm/constants/inputNames'
import { CustomTariffData, OrganizationCreateType } from '@/shared/types'
import { organizationsApi } from '@/shared/api'
import { showErrorMessage } from '@/shared/utils'
import { useOrganizationListing } from '@/shared/composables'
import { tariffsApi } from '@/shared/api/admin/tariffs.api'
import { ORGANIZATION_STATUSES } from '@/shared/constants/organization'

export default defineComponent({
  name: 'WOrganizationCreationForm',
  setup() {
    const { updateOrganizationListing } = useOrganizationListing()

    const router = useRouter()

    const organizationCreationForm = ref<FormInstance>()
    const checkBoxChoice = ref()

    const formInputRefs = reactive({
      nameRef: undefined,
      addressRef: undefined,
      phoneRef: undefined,
      siteRef: undefined,
      innRef: undefined,
      statusRef: undefined,
      partnerRef: undefined,
      isBlockedRef: undefined,
      blockAtRef: '',
      packageCountRef: undefined,
      modules: [],
      tariffIdRef: undefined,
      subscriptionTypeRef: undefined,
      useCustomTariff: undefined,
      customTariffReportCount: undefined,
      customTariffPriceMonth: undefined,
      customTariffPriceOver: undefined,
    })

    const tariffList = ref([])

    const organizationCreationFormData: OrganizationCreateType = reactive({
      name: '',
      address: '',
      phone: '',
      site: '',
      inn: '',
      isTest: null,
      logo: undefined,
      blockAt: '',
      packageCount: undefined,
      partner: null,
      status: null,
      modules: [],
      tariffId: null,
      subscriptionType: null,
      useCustomTariff: undefined,
      customTariffData: {
        report_count: 0,
        price_month: 0,
        price_over: 0,
        price_year: 0,
      },
      dealDate: null,
    })

    const defaultTariffId = ref(-1)

    const tariffsAreEqual = (
      tariff1: CustomTariffData,
      tariff2: CustomTariffData
    ): boolean => {
      return (
        tariff1.price_month == tariff2.price_month &&
        tariff1.price_over == tariff2.price_over &&
        tariff1.report_count == tariff2.report_count &&
        tariff1.price_year == tariff2.price_year
      )
    }

    watch(organizationCreationFormData, (newValue, oldValue) => {
      if (newValue.tariffId == defaultTariffId.value) {
        organizationCreationFormData.useCustomTariff = true
      }

      if (newValue.tariffId != defaultTariffId.value) {
        const newTariff = tariffList.value.find(
          (tariff) => tariff.id === organizationCreationFormData.tariffId
        )

        if (
          !newTariff ||
          tariffsAreEqual(
            organizationCreationFormData.customTariffData,
            newTariff
          )
        ) {
          organizationCreationFormData.useCustomTariff = false
          return
        }

        Object.assign(organizationCreationFormData, {
          customTariffData: {
            price_month: newTariff.price_month,
            price_over: newTariff.price_over,
            report_count: newTariff.report_count,
            price_year: newTariff.price_year,
          },
          useCustomTariff: false,
        })
      }
    })

    const getTariffs = async (): Promise<void> => {
      const [error, response] = await tariffsApi.getTariffsList()

      if (error) {
        showErrorMessage(error)
      } else {
        tariffList.value = response.data.tariffs
      }
    }

    const organizationCreationValidationRules = reactive<FormRules>({
      name: [{ required: true, message: 'Введите название', trigger: 'blur' }],
      address: [{ required: true, message: 'Введите адрес', trigger: 'blur' }],
      isTest: [
        {
          required: true,
          message: 'Выберите уровень доступа',
          trigger: 'blur',
        },
      ],
    })

    const images = ref<File[]>([])

    const clearForm = () => {
      organizationCreationForm.value.resetFields()
      images.value = []
    }

    const createOrganization = async (): Promise<number> => {
      organizationCreationFormData.logo = images.value[0]

      const [error, response] = await organizationsApi.createOrganization(
        organizationCreationFormData
      )

      if (error) {
        showErrorMessage(error)

        return 0
      }

      updateOrganizationListing(true)

      ElMessage({
        message: 'Организация добавлена',
        type: 'success',
      })

      clearForm()

      return response.data.organization_id
    }

    const handleChangeFocusedInput = (nextInput: string): void => {
      formInputRefs[nextInput].focus()
    }

    const handleUploadFile = (file: UploadFile): void => {
      images.value.push(file.raw)
    }

    const handleExceed: UploadProps['onExceed'] = () => {
      ElMessage({
        message: 'Невозможно загрузить больше одного логотипа',
        type: 'warning',
      })
    }

    const handleRemoveImage = (file: UploadUserFile): void => {
      images.value = images.value.filter((image) => image.name !== file.name)
    }

    const handleSubmitForm = (): void => {
      if (!organizationCreationForm) return

      organizationCreationForm.value.validate(
        async (valid): Promise<boolean> => {
          if (valid) {
            const organizationId = await createOrganization()

            if (organizationId) {
              router.push({
                name: 'OrganizationStatisticPage',
                params: { organizationId },
              })
            }
          }

          return
        }
      )
    }

    onMounted((): void => {
      getTariffs()
    })

    return {
      INPUT_NAMES,
      ...toRefs(formInputRefs),
      tariffList,
      organizationCreationForm,
      organizationCreationFormData,
      organizationCreationValidationRules,
      images,
      checkBoxChoice,
      ORGANIZATION_STATUSES,
      handleChangeFocusedInput,
      handleUploadFile,
      handleExceed,
      handleRemoveImage,
      handleSubmitForm,
    }
  },
})
